'use client'

import Link from 'next/link'
import { Coins, AlertTriangle } from 'lucide-react'
import { useFreemium } from '@/lib/freemium-context'
import { computeCost, displayCredits, PER_MINUTE_COSTS, type ModuleType } from '@/lib/credit-costs'
import { REFERENCE_PRICE } from '@/lib/pricing-display'

interface CreditCostBannerProps {
  moduleType: ModuleType
  /** Durée en minutes — modules à coût variable (transcription, correction). */
  minutes?: number
}

/**
 * Bandeau discret affichant le coût estimé d'une opération et le solde de
 * crédits du plan Pro. En cas de solde insuffisant ou épuisé, propose la
 * recharge vers /pricing.
 *
 * Ne s'affiche pas pour les comptes admin (illimité) ni en plan gratuit
 * (le blocage des modules y est géré directement par chaque module).
 */
export function CreditCostBanner({ moduleType, minutes }: CreditCostBannerProps) {
  const { limits, isAdmin } = useFreemium()

  if (isAdmin || !limits.is_pro) return null

  const credits = limits.credits
  const isVariable = moduleType === 'transcription' || moduleType === 'correction'
  const hasMinutes = minutes !== undefined && minutes > 0
  // Coût indéterminé tant que la durée n'est pas connue (transcription/correction).
  const cost = isVariable && !hasMinutes ? null : computeCost(moduleType, minutes)

  const exhausted = credits <= 0
  const insufficient = cost !== null && !exhausted && credits < cost

  // ── Solde épuisé — tous les modules sont bloqués ───────────────────────────
  if (exhausted) {
    return (
      <div
        role="alert"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
          borderRadius: 10, border: '1px solid #FECACA', background: '#FEF2F2',
        }}
      >
        <AlertTriangle size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#991B1B', margin: 0 }}>
            Vos crédits sont épuisés. Souscrivez à nouveau pour continuer.
          </p>
          <Link
            href="/pricing"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
              padding: '7px 14px', borderRadius: 8, background: '#DC2626', color: '#fff',
              fontSize: 12, fontWeight: 700, textDecoration: 'none',
            }}
          >
            {/* Prix de référence UNIQUE (D-020) — `amount` seul, sans période :
                la phrase porte déjà le volume de crédits. */}
            Recharger 1300 crédits — {REFERENCE_PRICE.amount}
          </Link>
        </div>
      </div>
    )
  }

  // ── Solde insuffisant pour cette opération ─────────────────────────────────
  if (insufficient) {
    return (
      <div
        role="alert"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
          borderRadius: 10, border: '1px solid #FECACA', background: '#FEF2F2',
        }}
      >
        <AlertTriangle size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#991B1B', margin: 0 }}>
            Crédits insuffisants. Rechargez votre plan Pro pour continuer.
          </p>
          <p style={{ fontSize: 12, color: '#B91C1C', margin: '2px 0 0' }}>
            Coût de l&apos;opération : {displayCredits(cost!)} crédits · Votre solde : {displayCredits(credits)} crédits
          </p>
          <Link
            href="/pricing"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
              padding: '7px 14px', borderRadius: 8, background: '#DC2626', color: '#fff',
              fontSize: 12, fontWeight: 700, textDecoration: 'none',
            }}
          >
            Recharger →
          </Link>
        </div>
      </div>
    )
  }

  // ── Message discret de coût estimé ─────────────────────────────────────────
  const costLabel =
    cost === null
      ? `environ ${PER_MINUTE_COSTS[moduleType as 'transcription' | 'correction']} crédit/minute`
      : `environ ${displayCredits(cost)} crédit${displayCredits(cost) > 1 ? 's' : ''}`

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        borderRadius: 8, border: '1px solid #E5E7EB', background: '#F9FAFB',
      }}
    >
      <Coins size={14} color="#6B7280" style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: '#6B7280' }}>
        Cette opération consommera {costLabel} — Votre solde :{' '}
        <strong style={{ color: '#374151' }}>{displayCredits(credits)} crédits</strong>
      </span>
    </div>
  )
}
